import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AirportCatalogService } from 'src/app/core/services/airport-catalog/airport-catalog.service';
import { StatusTicketCard } from '../models/StatusTicketCard';
import { TicketResponse } from '../models/Ticket';
import { FlightCrewSummaryResponse } from '../models/FlightCrewItem';
import { DestinationImageResolver } from '../flight-ticket-frame/destination-image.resolver';
import { CityLookup, FlightTicketTone, IMAGE_RETRY_MS } from '../flight-ticket-frame/flight-ticket-frame.component';
import { HeaderSchedule, ItinerarySummary, SeatSummary, headerSchedule, summarizeCardItinerary, summarizeSeats, ticketTone } from '../flight-ticket-frame/flight-ticket.util';
import { StandbyDetailComponent } from '../standby-detail/standby-detail.component';

/**
 * Body of a booked ticket (Meus Voos) inside the shared FlightTicketFrame.
 * Presentation only: copying and cancelling are delegated to the card that owns the reservation.
 */
@Component({
  selector: 'app-booked-flight-ticket',
  templateUrl: './booked-flight-ticket.component.html',
  styleUrls: ['./booked-flight-ticket.component.scss'],
  standalone: false
})
export class BookedFlightTicketComponent implements OnInit, OnChanges, OnDestroy {
  readonly StatusTicketCard = StatusTicketCard;

  private readonly imageResolver = inject(DestinationImageResolver);
  private readonly airportCatalog = inject(AirportCatalogService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly dialog = inject(MatDialog);

  @Input() tickets: TicketResponse[] = [];
  @Input() status: StatusTicketCard;

  /** Reservation code (PNR) to copy. */
  @Output() copyValue = new EventEmitter<string>();
  @Output() copyTicketNumber = new EventEmitter<string>();
  @Output() cancelRequested = new EventEmitter<void>();

  itinerary: ItinerarySummary = summarizeCardItinerary([]);
  /** Final arrival of this ordered reservation, used only to select the card photo. */
  imageDestination: string | null = null;
  /** This thumbnail failed on its own. Other cards keep the photo until the failure is confirmed. */
  thumbnailFailed = false;
  private retried = false;
  private retryTimer?: ReturnType<typeof setTimeout>;
  schedule: HeaderSchedule = headerSchedule([]);

  readonly cityOf: CityLookup = iata => this.airportCatalog.cityName(iata);

  ngOnInit(): void {
    this.imageResolver.prepare();
  }

  ngOnChanges(): void {
    const legs = (this.tickets ?? []).map(ticket => ({
      origin: ticket.departure,
      destination: ticket.arrival,
      departure: ticket.departureDateLt,
      arrival: ticket.arrivalDateLt
    }));
    this.thumbnailFailed = false;
    this.retried = false;
    clearTimeout(this.retryTimer);
    this.itinerary = summarizeCardItinerary(legs);
    this.imageDestination = legs.at(-1)?.destination?.trim() || null;
    this.schedule = headerSchedule(legs);
  }

  ngOnDestroy(): void {
    clearTimeout(this.retryTimer);
  }

  get first(): TicketResponse | undefined {
    return this.tickets?.[0];
  }

  get isActive(): boolean {
    return this.status === StatusTicketCard.ACTIVE;
  }

  get tone(): FlightTicketTone {
    return ticketTone(this.status);
  }

  get imageUrl(): string | null {
    return this.imageResolver.resolve(this.imageDestination);
  }

  get imageAlt(): string {
    const destination = this.imageDestination;
    if (!destination) return '';
    return `Destino ${this.airportCatalog.cityName(destination) ?? destination}`;
  }

  get flightNumbers(): string {
    return (this.tickets ?? []).map(ticket => `LA${ticket.flightNumber}`).join(' / ');
  }

  /**
   * Compact cards (Cancelados / Histórico). Origin plus the destination already chosen by
   * summarizeItinerary; when that destination is unknown, the real stop list is shown as-is.
   */
  get compactRoute(): string {
    const stops = this.itinerary.stops.filter(code => !!code);
    const destination = this.itinerary.destination;
    if (!stops.length) return '';
    if (!destination) return stops.join(' → ');
    return stops[0] === destination ? stops[0] : `${stops[0]} → ${destination}`;
  }

  /** Connection airports of a compact card ("via BSB"), empty for a direct flight. */
  get compactVia(): string {
    const stops = this.itinerary.stops.filter(code => !!code);
    return this.itinerary.destination && stops.length > 2 ? stops.slice(1, -1).join(', ') : '';
  }

  /**
   * Histórico only. `flightStatus` (tickets.flight_status) is tri-state and `false` is a real
   * answer, so it is compared explicitly: true → used, false → unused, null/undefined → unknown.
   * A PNR with several legs stays unknown while any leg has not been evaluated, and is used only
   * when every leg is true. Outside Histórico there is no usage badge.
   */
  get usage(): 'used' | 'unused' | 'unknown' | null {
    if (this.status !== StatusTicketCard.INACTIVE) return null;
    const statuses = (this.tickets ?? []).map(ticket => ticket.flightStatus);
    if (!statuses.length || statuses.some(value => value !== true && value !== false)) return 'unknown';
    return statuses.every(value => value === true) ? 'used' : 'unused';
  }

  get seats(): SeatSummary | null {
    return summarizeSeats(this.tickets ?? []);
  }

  standbyTotal(summary: FlightCrewSummaryResponse): number {
    return (summary.totalLatam ?? 0) + (summary.totalExternal ?? 0);
  }

  openStandbyDetail(summary: FlightCrewSummaryResponse): void {
    this.dialog.open(StandbyDetailComponent, {
      data: summary,
      width: '440px',
      maxWidth: 'calc(100vw - 24px)',
      panelClass: 'standby-detail-panel',
      ariaLabelledBy: 'standby-detail-title',
      ariaDescribedBy: 'standby-detail-subtitle'
    });
  }

  onImageError(url: string): void {
    this.thumbnailFailed = true;
    if (this.retried) {
      this.imageResolver.reportLoadError(url);
      return;
    }
    this.retried = true;
    this.retryTimer = setTimeout(() => {
      this.thumbnailFailed = false;
      this.changeDetector.markForCheck();
    }, IMAGE_RETRY_MS);
  }
}
